import { useEffect, useMemo, useState } from 'react'
import { useReducedMotion } from '@/hooks/use-reduced-motion'

interface TypewriterOptions {
    speed?: number
    startDelay?: number
}

interface TypewriterState {
    lines: Array<string>
    started: boolean
    done: boolean
    activeLine: number
}

/** Types a list of lines out character by character, line by line. */
export const useTypewriter = (
    source: Array<string>,
    active: boolean,
    { speed = 18, startDelay = 900 }: TypewriterOptions = {}
): TypewriterState => {
    const reduced = useReducedMotion()
    const total = useMemo(() => source.reduce((sum, line) => sum + line.length, 0), [source])
    const [count, setCount] = useState(0)
    const [started, setStarted] = useState(false)

    useEffect(() => {
        setCount(0)
        setStarted(false)
    }, [source])

    useEffect(() => {
        if (!active) return

        if (reduced) {
            setStarted(true)
            setCount(total)
            return
        }

        let interval: ReturnType<typeof setInterval> | undefined

        const timeout = setTimeout(() => {
            setStarted(true)

            interval = setInterval(() => {
                setCount(current => {
                    if (current >= total) {
                        if (interval) clearInterval(interval)
                        return current
                    }

                    return current + 1
                })
            }, speed)
        }, startDelay)

        return () => {
            clearTimeout(timeout)
            if (interval) clearInterval(interval)
        }
    }, [active, reduced, total, speed, startDelay])

    let remaining = count
    let activeLine = 0

    const lines = source.map((line, index) => {
        const visible = line.slice(0, Math.max(0, remaining))

        if (remaining > 0) activeLine = index
        remaining -= line.length

        return visible
    })

    return { lines, started, done: count >= total, activeLine }
}
